import { parseAgentEvent } from './agent-events';
import type { AgentEvent } from './types';

/**
 * An incremental `text/event-stream` frame parser.
 *
 * Kept free of streams and of the DOM: every case worth testing — a frame torn
 * mid-JSON, a `: ping` heartbeat, a multi-line `data:` — is a string problem,
 * and a parser that needs a `ReadableStream` to test is a parser nobody tests.
 */
export function createSseParser(): { push(chunk: string): string[]; flush(): string[] } {
  let buffer = '';

  const frame = (raw: string): string | null => {
    const data: string[] = [];
    for (const line of raw.split('\n')) {
      // A line starting with ":" is a comment. The backend's heartbeat is
      // `: ping`, sent whenever a turn has produced nothing for 25 seconds.
      if (line.startsWith(':')) continue;
      if (!line.startsWith('data:')) continue;
      data.push(line.slice('data:'.length).replace(/^ /, ''));
    }
    return data.length > 0 ? data.join('\n') : null;
  };

  const drain = (text: string): string[] => {
    const out: string[] = [];
    for (const raw of text.split('\n\n')) {
      const payload = frame(raw);
      if (payload !== null) out.push(payload);
    }
    return out;
  };

  return {
    push(chunk: string): string[] {
      buffer += chunk.replace(/\r\n/g, '\n');
      const parts = buffer.split('\n\n');
      // The last part is whatever came after the final blank line: an
      // incomplete frame, kept for the next chunk.
      buffer = parts.pop() ?? '';
      return parts.flatMap((raw) => {
        const payload = frame(raw);
        return payload === null ? [] : [payload];
      });
    },
    flush(): string[] {
      const rest = buffer;
      buffer = '';
      return rest.trim() ? drain(rest) : [];
    },
  };
}

/**
 * The events of one streaming response, typed.
 *
 * Anything that does not parse as JSON, or that `parseAgentEvent` does not
 * recognise, is dropped rather than thrown: a turn must survive one bad frame,
 * and a backend that grows a new event type must not break an older tab.
 */
export async function* streamAgentEvents(
  response: Response,
  signal?: AbortSignal,
): AsyncGenerator<AgentEvent> {
  if (!response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();

  const typed = function* (payloads: string[]): Generator<AgentEvent> {
    for (const payload of payloads) {
      let raw: unknown;
      try {
        raw = JSON.parse(payload);
      } catch {
        continue;
      }
      const event = parseAgentEvent(raw);
      if (event) yield event;
    }
  };

  try {
    for (;;) {
      if (signal?.aborted) {
        // `releaseLock()` below only detaches this reader — it does not tear
        // down the response body. `reader.cancel()` is what actually aborts
        // the underlying stream (and, on a real fetch, signals the server to
        // stop generating). In practice the caller's own `AbortSignal` is
        // also passed to the `fetch` that produced this `Response`, so the
        // connection is usually already being torn down by the time this
        // runs; cancelling here as well makes that not a load-bearing
        // assumption. A stream that already closed or errored rejects
        // `cancel()` — the caller is stopping either way, so that's ignored.
        await reader.cancel().catch(() => {});
        return;
      }
      const { done, value } = await reader.read();
      if (done) break;
      yield* typed(parser.push(decoder.decode(value, { stream: true })));
    }
    yield* typed(parser.flush());
  } finally {
    // Detach the reader so the stream is never left locked to a generator
    // nobody is iterating any more.
    reader.releaseLock();
  }
}
